import {
  AuthResponse,
  LoginCredentials,
  RegisterCredentials,
  User,
} from "@/types/auth";
import { tokenStorage } from "@/utils/tokenStorage";

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Create axios-like fetch wrapper with timeout
const apiRequest = async (
  endpoint: string,
  options: RequestInit = {},
): Promise<any> => {
  const token = tokenStorage.getToken();

  const config: RequestInit = {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(token && {
        Authorization: `Bearer ${token}`,
        "X-My-Secret": "letmein",
      }),
      ...options.headers,
    },
    ...options,
  };

  // Add timeout to prevent infinite loading
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...config,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ message: "Network error" }));

      // Map HTTP status codes to user-friendly messages
      let userFriendlyMessage = errorData.message;

      if (response.status === 401) {
        userFriendlyMessage = "Invalid credentials or session expired";
      } else if (response.status === 422) {
        userFriendlyMessage =
          errorData.message || "Please check your input and try again";
      } else if (response.status === 404) {
        userFriendlyMessage = "Service not found. Please try again later";
      } else if (response.status === 429) {
        userFriendlyMessage =
          "Too many requests. Please wait a moment and try again";
      } else if (response.status >= 500) {
        userFriendlyMessage = "Server error. Please try again later";
      } else if (
        !userFriendlyMessage ||
        userFriendlyMessage.includes("HTTP error")
      ) {
        userFriendlyMessage = "Something went wrong. Please try again";
      }

      throw new Error(userFriendlyMessage);
    }

    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === "AbortError") {
      throw new Error(
        "Request timeout. Please check your internet connection.",
      );
    }
    throw error;
  }
};

// Auth Service Functions
export const authService = {
  // Get user profile
  async getProfile(): Promise<User> {
    try {
      const response = await apiRequest("/profile");

      // Transform Laravel user to frontend User type
      return this.transformUserResponse(response);
    } catch (error) {
      console.error("Get profile error:", error);
      throw error;
    }
  },

  // Get user profile with specific token
  async getProfileWithToken(token: string): Promise<User> {
    try {
      // Set the token temporarily so apiRequest can use it
      const originalToken = tokenStorage.getToken();
      const wasRememberMe = tokenStorage.isRememberMeEnabled();
      tokenStorage.setToken(token, true); // Temporarily use localStorage

      const response = await apiRequest("/profile");

      // Restore original token
      if (originalToken) {
        tokenStorage.setToken(originalToken, wasRememberMe);
      } else {
        tokenStorage.removeToken();
      }

      // Transform Laravel user to frontend User type
      return this.transformUserResponse(response);
    } catch (error) {
      console.error("Get profile error:", error);
      throw error;
    }
  },

  // Google login
  async googleLogin(googleToken: string): Promise<AuthResponse> {
    try {
      const response = await apiRequest("/google-login", {
        body: JSON.stringify({
          token: googleToken,
        }),
        method: "POST",
      });

      if (response.access_token) {
        tokenStorage.setToken(response.access_token, true); // Google login always persists

        const userProfile = await this.getProfile();

        return {
          message: "Google login successful",
          success: true,
          token: response.access_token,
          user: userProfile,
        };
      }

      throw new Error("Invalid Google login response");
    } catch (error) {
      console.error("Google login error:", error);
      return {
        message: error instanceof Error ? error.message : "Google login failed",
        success: false,
      };
    }
  },

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return tokenStorage.hasToken();
  },

  // Login user
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await apiRequest("/login", {
        body: JSON.stringify(credentials),
        method: "POST",
      });

      // Check for different possible token field names
      const token =
        response.token ||
        response.access_token ||
        response.data?.access_token ||
        response.data?.token;

      if (token) {
        // Store the token with rememberMe preference
        tokenStorage.setToken(token, credentials.rememberMe ?? false);

        // Get user profile with the token
        const userProfile = await this.getProfileWithToken(token);

        return {
          message: response.message || "Login successful",
          success: true,
          token: token,
          user: userProfile,
        };
      }

      console.error(
        "No token found in response. Response structure:",
        Object.keys(response),
      );
      throw new Error("Invalid login response - no token found");
    } catch (error) {
      console.error("Login error:", error);

      // Demo fallback for testing - remove this in production
      if (
        credentials.email === "admin@demo.com" &&
        credentials.password === "password"
      ) {
        const demoUser = {
          avatar: undefined,
          createdAt: new Date().toISOString(),
          email: "admin@demo.com",
          firstName: "Admin",
          id: "1",
          isEmailVerified: true,
          lastLoginAt: new Date().toISOString(),
          lastName: "User",
          role: "admin" as const,
        };

        const demoToken = "demo-token-" + Date.now();
        tokenStorage.setToken(demoToken, credentials.rememberMe ?? false);

        return {
          message: "Demo login successful",
          success: true,
          token: demoToken,
          user: demoUser,
        };
      }

      if (
        credentials.email === "driver@demo.com" &&
        credentials.password === "password"
      ) {
        const demoUser = {
          avatar: undefined,
          createdAt: new Date().toISOString(),
          email: "driver@demo.com",
          firstName: "Demo",
          id: "2",
          isEmailVerified: true,
          lastLoginAt: new Date().toISOString(),
          lastName: "Driver",
          role: "driver" as const,
        };

        const demoToken = "demo-token-" + Date.now();
        tokenStorage.setToken(demoToken, credentials.rememberMe ?? false);

        return {
          message: "Demo login successful",
          success: true,
          token: demoToken,
          user: demoUser,
        };
      }

      return {
        message: error instanceof Error ? error.message : "Login failed",
        success: false,
      };
    }
  },

  // Logout user
  async logout(): Promise<void> {
    try {
      await apiRequest("/logout", {
        method: "POST",
      });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      tokenStorage.removeToken();
    }
  },

  // Transform Laravel user response to frontend User type
  // Map API role strings to frontend role literals
  normalizeRole(
    raw: string,
  ):
    | "admin"
    | "driver"
    | "employee"
    | "manager"
    | "partner"
    | "staff"
    | "user" {
    const map: Record<
      string,
      "admin" | "driver" | "employee" | "manager" | "partner" | "staff" | "user"
    > = {
      admin: "admin",
      Admin: "admin",
      deliverydriver: "driver",
      DeliveryDriver: "driver",
      driver: "driver",
      Driver: "driver",
      employee: "employee",
      Employee: "employee",
      manager: "manager",
      Manager: "manager",
      partner: "partner",
      Partner: "partner",
      staff: "staff",
      Staff: "staff",
      user: "user",
      User: "user",
    };
    return map[raw] ?? "user";
  },

  // Refresh token
  async refreshToken(): Promise<null | string> {
    try {
      const response = await apiRequest("/refresh", {
        method: "POST",
      });

      if (response.access_token) {
        // Preserve rememberMe preference when refreshing
        const rememberMe = tokenStorage.isRememberMeEnabled();
        tokenStorage.setToken(response.access_token, rememberMe);
        return response.access_token;
      }

      return null;
    } catch (error) {
      console.error("Token refresh error:", error);
      tokenStorage.removeToken();
      return null;
    }
  },

  // Register user
  async register(credentials: RegisterCredentials): Promise<AuthResponse> {
    try {
      const response = await apiRequest("/register", {
        body: JSON.stringify({
          email: credentials.email,
          first_name: credentials.firstName,
          last_name: credentials.lastName,
          password: credentials.password,
          password_confirmation: credentials.confirmPassword,
          role: credentials.role || "user",
        }),
        method: "POST",
      });

      if (response.access_token) {
        tokenStorage.setToken(response.access_token, false); // Default to session storage for registration

        const userProfile = await this.getProfile();

        return {
          message: "Registration successful",
          success: true,
          token: response.access_token,
          user: userProfile,
        };
      }

      return {
        message: "Registration successful. Please verify your email.",
        success: true,
      };
    } catch (error) {
      console.error("Registration error:", error);
      return {
        message: error instanceof Error ? error.message : "Registration failed",
        success: false,
      };
    }
  },

  // Verify reset code and update password in one step
  async resetPasswordWithCode(
    email: string,
    code: string,
    password: string,
    confirmPassword: string,
  ): Promise<{ message: string; success: boolean }> {
    try {
      // Call Laravel API directly with email, code, and password
      const response = await apiRequest("/password/update-password", {
        body: JSON.stringify({
          code,
          email,
          password,
          password_confirmation: confirmPassword,
        }),
        method: "POST",
      });

      return {
        message: response.message || "Password updated successfully",
        success: true,
      };
    } catch (error) {
      return {
        message:
          error instanceof Error ? error.message : "Failed to reset password",
        success: false,
      };
    }
  },

  // Send password reset code
  async sendResetCode(
    email: string,
  ): Promise<{ message: string; success: boolean }> {
    try {
      const response = await apiRequest("/password/send-reset-code", {
        body: JSON.stringify({ email }),
        method: "POST",
      });

      return {
        message: response.message || "Reset code sent to your email",
        success: true,
      };
    } catch (error) {
      let errorMessage = "Failed to send reset code";

      if (error instanceof Error) {
        if (
          error.message.includes("not found") ||
          error.message.includes("404")
        ) {
          errorMessage = "Email address not found";
        } else if (
          error.message.includes("too many") ||
          error.message.includes("429")
        ) {
          errorMessage =
            "Too many reset attempts. Please wait before trying again";
        } else {
          errorMessage = error.message;
        }
      }

      return {
        message: errorMessage,
        success: false,
      };
    }
  },

  transformUserResponse(response: any): User {
    // Parse permissions if they exist (could be string or array)
    let permissions: string[] | undefined;
    if (response.permissions) {
      if (typeof response.permissions === "string") {
        try {
          permissions = JSON.parse(response.permissions);
          if (!Array.isArray(permissions)) permissions = [];
        } catch {
          permissions = [];
        }
      } else if (Array.isArray(response.permissions)) {
        permissions = response.permissions;
      }
    }

    return {
      avatar: response.avatar || response.profile_pic,
      createdAt: response.created_at,
      email: response.email,
      firstName: response.first_name || response.name || "",
      id: response.id.toString(),
      isEmailVerified: !!response.email_verified_at,
      lastLoginAt: response.last_login_at,
      lastName: response.last_name || response.surname || "",
      permissions,
      role: this.normalizeRole(response.role || "user"),
      // Add driver profile if it exists
      ...(response.driver_profile && {
        driverProfile: {
          applicationStatus:
            response.driver_profile.application_status || "pending",
          city: response.driver_profile.city,
          documentsSubmitted: !!response.driver_profile.documents_submitted,
          hasValidLicense: !!response.driver_profile.has_valid_license,
          hasVehicleAccess: !!response.driver_profile.has_vehicle_access,
          id: response.driver_profile.id.toString(),
          lastApplicationDate: response.driver_profile.last_application_date,
          phoneNumber: response.driver_profile.phone_number,
          profileCompleted: !!response.driver_profile.profile_completed,
          rejectionReason: response.driver_profile.rejection_reason,
          vehicleType: response.driver_profile.vehicle_type,
        },
      }),
    };
  },

  // Update password with reset token
  async updatePassword(
    token: string,
    password: string,
    confirmPassword: string,
  ): Promise<{ message: string; success: boolean }> {
    try {
      const response = await apiRequest("/password/update-password", {
        body: JSON.stringify({
          password,
          password_confirmation: confirmPassword,
          token,
        }),
        method: "POST",
      });

      return {
        message: response.message || "Password updated successfully",
        success: true,
      };
    } catch (error) {
      let errorMessage = "Failed to update password";

      if (error instanceof Error) {
        if (
          error.message.includes("token") &&
          error.message.includes("invalid")
        ) {
          errorMessage = "Reset session expired. Please start over";
        } else if (
          error.message.includes("password") &&
          error.message.includes("weak")
        ) {
          errorMessage =
            "Password is too weak. Please choose a stronger password";
        } else if (error.message.includes("match")) {
          errorMessage = "Passwords do not match";
        } else if (error.message.includes("confirmation")) {
          errorMessage = "Password confirmation is required";
        } else if (
          error.message.includes("too short") ||
          error.message.includes("length")
        ) {
          errorMessage = "Password must be at least 8 characters long";
        } else {
          errorMessage = error.message;
        }
      }

      return {
        message: errorMessage,
        success: false,
      };
    }
  },

  // Validate token and get user
  async validateToken(): Promise<null | User> {
    try {
      const token = tokenStorage.getToken();
      if (!token) {
        return null;
      }

      // Handle demo tokens
      if (token.startsWith("demo-token-")) {
        if (token.includes("admin")) {
          return {
            avatar: undefined,
            createdAt: new Date().toISOString(),
            email: "admin@demo.com",
            firstName: "Admin",
            id: "1",
            isEmailVerified: true,
            lastLoginAt: new Date().toISOString(),
            lastName: "User",
            role: "admin" as const,
          };
        } else {
          return {
            avatar: undefined,
            createdAt: new Date().toISOString(),
            email: "driver@demo.com",
            firstName: "Demo",
            id: "2",
            isEmailVerified: true,
            lastLoginAt: new Date().toISOString(),
            lastName: "Driver",
            role: "driver" as const,
          };
        }
      }

      return await this.getProfile();
    } catch (error) {
      console.error("Token validation error:", error);
      tokenStorage.removeToken();
      return null;
    }
  },

  // Verify reset code
  async verifyResetCode(
    email: string,
    code: string,
  ): Promise<{ message: string; success: boolean; token?: string }> {
    try {
      const response = await apiRequest("/password/verify-reset-code", {
        body: JSON.stringify({ code, email }),
        method: "POST",
      });

      return {
        message: response.message || "Code verified successfully",
        success: true,
        token: response.reset_token || response.token,
      };
    } catch (error) {
      let errorMessage = "Invalid reset code";

      if (error instanceof Error) {
        if (error.message.includes("expired")) {
          errorMessage = "Reset code has expired. Please request a new one";
        } else if (
          error.message.includes("invalid") ||
          error.message.includes("wrong")
        ) {
          errorMessage = "Invalid reset code. Please check and try again";
        } else if (
          error.message.includes("too many") ||
          error.message.includes("429")
        ) {
          errorMessage = "Too many attempts. Please wait before trying again";
        } else if (!error.message.includes("reset code")) {
          errorMessage = error.message;
        }
      }

      return {
        message: errorMessage,
        success: false,
      };
    }
  },
};

export default authService;
